import React from 'react';
import clsx from 'clsx';
import Icon from '@/components/icon/Icon';
import { imageTextThemeClasses } from '@/components/image-text/theme';
import type {
  BaseImageTextProps,
  ImageLegendItemProps,
} from '@/components/image-text/types';

/**
 * The key to an annotated image: one row per mark drawn on the artwork.
 *
 * Deliberately imported by file path rather than through the section barrel —
 * const.ts pulls the variants in to build the variant map, so anything a
 * variant reaches for has to stay off that path (see the note in theme.ts).
 */
type ImageLegendProps = {
  items: ImageLegendItemProps[];
  theme: NonNullable<BaseImageTextProps['theme']>;
};

export const ImageLegend: React.FC<ImageLegendProps> = ({ items, theme }) => {
  // An editor can leave a row half-filled; a swatch with nothing to explain is
  // noise, so the label is what decides whether the row exists.
  const rows = items.filter((item) => item.label);

  if (!rows.length) {
    return null;
  }

  return (
    <ul
      className={clsx(
        'flex flex-wrap items-center gap-x-6 gap-y-2 border-t px-4 py-3',
        imageTextThemeClasses.legendBorder(theme),
        imageTextThemeClasses.legend(theme),
      )}
    >
      {rows.map((item, index) => (
        <li
          key={index}
          className="flex items-center gap-2 text-xs font-semibold"
        >
          {item.iconName && (
            <Icon name={item.iconName} size={20} className="shrink-0" />
          )}
          {item.label}
        </li>
      ))}
    </ul>
  );
};
